import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

const SUITS = ["Spades", "Hearts", "Diamonds", "Clubs"] as const;

const SUIT_SYMBOL: Record<(typeof SUITS)[number], string> = {
  Spades: "♠",
  Hearts: "♥",
  Diamonds: "♦",
  Clubs: "♣",
};

const SUIT_LABEL: Record<(typeof SUITS)[number], string> = {
  Spades: "Spades",
  Hearts: "Hearts",
  Diamonds: "Diamonds",
  Clubs: "Clubs",
};

const SUIT_COLOR: Record<(typeof SUITS)[number], string> = {
  Spades: "#e2e8f0",
  Hearts: "#fb7185",
  Diamonds: "#fb7185",
  Clubs: "#e2e8f0",
};

interface Props {
  isCaller: boolean;
  callerName: string;
  stake: number;
  onPickTrump: (suit: string) => void;
}

const item = {
  hidden: { opacity: 0, y: 16, rotate: -6, scale: 0.92 },
  show: {
    opacity: 1,
    y: 0,
    rotate: 0,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 420, damping: 28 },
  },
};

export const TrumpPickPanel = ({ isCaller, callerName, stake: _stake, onPickTrump }: Props) => {
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <Box
      sx={{
        background: "linear-gradient(165deg, rgba(15,23,42,0.92) 0%, rgba(30,41,59,0.88) 100%)",
        borderRadius: 3,
        padding: { xs: 2, sm: 3 },
        textAlign: "center",
        color: "white",
        width: "100%",
        maxWidth: { sm: 440 },
        boxSizing: "border-box",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 24px 48px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2.5,
          fontSize: { xs: "1.05rem", sm: "1.3rem" },
          fontWeight: 800,
          letterSpacing: 0.3,
          textShadow: "0 2px 12px rgba(0,0,0,0.5)",
        }}
      >
        {isCaller ? "Pick your trump suit" : `Waiting for ${callerName} to pick trump…`}
      </Typography>

      {isCaller && (
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}
        >
          {SUITS.map((suit, idx) => (
            <motion.div
              key={suit}
              variants={reduceMotion ? undefined : item}
              initial={reduceMotion ? false : "hidden"}
              animate={reduceMotion ? undefined : "show"}
              transition={reduceMotion ? undefined : { delay: idx * 0.07 }}
              style={{ display: "inline-block" }}
            >
              <Box
                component="button"
                type="button"
                aria-label={`${SUIT_LABEL[suit]} (${SUIT_SYMBOL[suit]})`}
                title={`Pick ${SUIT_LABEL[suit]} as trump`}
                onClick={() => onPickTrump(suit)}
                sx={{
                  p: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  borderRadius: 2,
                  lineHeight: 1,
                  transition: "filter 0.2s",
                  "&:focus-visible": {
                    outline: "2px solid rgba(255,215,0,0.8)",
                    outlineOffset: 4,
                  },
                }}
              >
                <motion.div
                  whileHover={reduceMotion ? undefined : { y: -8, scale: 1.04 }}
                  whileTap={reduceMotion ? undefined : { scale: 0.96 }}
                  transition={{ type: "spring", stiffness: 400, damping: 22 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: { xs: 68, sm: 76 },
                    }}
                  >
                    <Typography
                      component="span"
                      aria-hidden
                      sx={{
                        fontSize: { xs: "3.25rem", sm: "3.75rem" },
                        fontWeight: 700,
                        lineHeight: 1,
                        color: SUIT_COLOR[suit],
                        textShadow: "0 4px 20px rgba(0,0,0,0.55), 0 0 1px rgba(0,0,0,0.8)",
                        userSelect: "none",
                      }}
                    >
                      {SUIT_SYMBOL[suit]}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.78)",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                      }}
                    >
                      {SUIT_LABEL[suit]}
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          ))}
        </Box>
      )}
    </Box>
  );
};
